/**
 * 动态加载和插入HTML组件
 * 支持组件缓存、配置和生命周期钩子
 */
class ComponentLoader {
  constructor() {
    this.components = {
      header: {
        path: 'templates/partials/header.html',
        selector: '#header-container',
        cache: true,
        priority: 1, // 加载优先级
      },
      footer: {
        path: 'templates/partials/footer.html',
        selector: '#footer-container',
        cache: true,
        priority: 2,
      },
    };
    this.cache = new Map(); // 组件缓存
    this.loadedComponents = new Set(); // 已加载的组件
  }

  /**
   * 注册新组件
   * @param {string} name - 组件名称
   * @param {string|Object} config - 组件路径或配置对象
   */
  register(name, config) {
    if (typeof config === 'string') {
      this.components[name] = {
        path: config,
        selector: `#${name}-container`,
        cache: true,
        priority: 10,
      };
    } else {
      this.components[name] = {
        selector: `#${name}-container`,
        priority: 10,
        ...config,
      };
    }
  }

  /**
   * 加载单个组件
   * @param {string} componentName - 组件名称
   * @param {string} [targetSelector] - 目标选择器（可选，覆盖默认）
   * @returns {Promise<string|null>} HTML 内容
   */
  async loadComponent(componentName, targetSelector = null) {
    const component = this.components[componentName];
    
    if (!component) {
      console.warn(`ComponentLoader: Component "${componentName}" not found`);
      return null;
    }

    // 检查缓存
    if (component.cache && this.cache.has(componentName)) {
      const cachedHtml = this.cache.get(componentName);
      await this.insertComponent(componentName, cachedHtml, targetSelector || component.selector);
      return cachedHtml;
    }

    try {
      const response = await fetch(component.path);
      if (!response.ok) {
        throw new Error(`Failed to load ${componentName} component: ${response.statusText}`);
      }
      
      const html = await response.text();
      
      // 缓存组件
      if (component.cache) {
        this.cache.set(componentName, html);
      }
      
      // 插入组件
      await this.insertComponent(componentName, html, targetSelector || component.selector);
      
      // 标记为已加载
      this.loadedComponents.add(componentName);
      
      // 触发加载完成事件
      this.onComponentLoaded(componentName, html);
      
      return html;
    } catch (error) {
      console.error(`ComponentLoader: Error loading ${componentName} component:`, error);
      return null;
    }
  }

  /**
   * 插入组件到 DOM
   * @param {string} componentName - 组件名称
   * @param {string} html - HTML 内容
   * @param {string} selector - 目标选择器
   */
  async insertComponent(componentName, html, selector) {
    const targetElement = document.querySelector(selector);
    
    if (!targetElement) {
      // 如果目标元素不存在，尝试创建
      const container = document.createElement('div');
      container.id = selector.replace('#', '');
      container.setAttribute('data-component', componentName);
      document.body.appendChild(container);
      
      container.innerHTML = html;
      return;
    }
    
    targetElement.innerHTML = html;
    targetElement.setAttribute('data-component', componentName);
  }

  /**
   * 组件加载完成回调
   * @param {string} componentName - 组件名称
   * @param {string} html - HTML 内容
   */
  onComponentLoaded(componentName, html) {
    // 可以在这里添加自定义逻辑
    // 例如：初始化组件内的脚本、触发事件等
    const event = new CustomEvent('componentLoaded', {
      detail: { componentName, html },
    });
    document.dispatchEvent(event);
  }

  /**
   * 加载所有组件
   * @param {Object} [options] - 选项
   * @param {Array<string>} [options.only] - 只加载指定的组件
   * @param {Array<string>} [options.exclude] - 排除的组件
   * @param {boolean} [options.parallel=true] - 是否并行加载
   */
  async loadAllComponents(options = {}) {
    const {
      only = null,
      exclude = [],
      parallel = true,
    } = options;

    // 确保必要的容器存在
    this.ensureContainers();

    // 获取要加载的组件列表
    let componentsToLoad = Object.keys(this.components)
      .filter(name => !exclude.includes(name))
      .filter(name => only === null || only.includes(name));

    // 按优先级排序
    componentsToLoad = componentsToLoad.sort((a, b) => {
      const priorityA = this.components[a].priority || 10;
      const priorityB = this.components[b].priority || 10;
      return priorityA - priorityB;
    });

    // 加载组件
    if (parallel) {
      await Promise.all(
        componentsToLoad.map(name => this.loadComponent(name))
      );
    } else {
      for (const name of componentsToLoad) {
        await this.loadComponent(name);
      }
    }

    document.body.classList.add('components-loaded');
  }

  /**
   * 确保必要的容器存在
   */
  ensureContainers() {
    const body = document.body;

    // Header 容器
    let headerContainer = document.querySelector('#header-container');
    if (!headerContainer) {
      headerContainer = document.createElement('div');
      headerContainer.id = 'header-container';
      body.insertBefore(headerContainer, body.firstChild);
    } else if (headerContainer !== body.firstElementChild) {
      body.insertBefore(headerContainer, body.firstChild);
    }

    // Footer 容器
    let footerContainer = document.querySelector('#footer-container');
    if (!footerContainer) {
      footerContainer = document.createElement('div');
      footerContainer.id = 'footer-container';
      body.appendChild(footerContainer);
    }
  }

  /**
   * 清除组件缓存
   * @param {string} [componentName] - 组件名称（可选，不传则清除所有）
   */
  clearCache(componentName = null) {
    if (componentName) {
      this.cache.delete(componentName);
      this.loadedComponents.delete(componentName);
    } else {
      this.cache.clear();
      this.loadedComponents.clear();
    }
  }

  /**
   * 检查组件是否已加载
   * @param {string} componentName - 组件名称
   * @returns {boolean}
   */
  isLoaded(componentName) {
    return this.loadedComponents.has(componentName);
  }

  /**
   * 获取组件配置
   * @param {string} componentName - 组件名称
   * @returns {Object|null}
   */
  getComponentConfig(componentName) {
    return this.components[componentName] || null;
  }
}

// 创建单例实例
export const componentLoader = new ComponentLoader();
